import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLicenseInfo } from '../config/vehicleLimitConfig';
import { isOperatorRole } from '../redux/slices/authSlice';
import { fetchSettings } from '../redux/slices/settingsSlice';
import { fetchOperators } from '../redux/slices/operatorsSlice';
import { isVehicleActive } from '../utils/vehicleActive';

/**
 * Kontingent-/Lizenzstatus der Fahrzeugverwaltung.
 *
 * Sub-Unternehmer sehen ihr eigenes, von der Zentrale vergebenes Kontingent
 * (`operators/{id}.vehicleLimit`), zentrale Konten das globale Limit aus
 * `appSettings/general`. Gezählt werden nur aktive Fahrzeuge — Reserve-
 * fahrzeuge (`active === false`) belegen keinen Platz, genau wie in der CF
 * `loadVehicleQuotaUsage`, die beim Anlegen/Aktivieren verbindlich prüft.
 *
 * `appData.vehicles` lädt der AppDataManager app-weit; Einstellungen und (für
 * Unternehmer-Konten) den eigenen Unternehmensdatensatz lädt der Hook nach,
 * falls die Seite direkt aufgerufen wurde.
 */
const useVehicleQuotaStatus = () => {
  const dispatch = useDispatch();
  const settingsInitialized = useSelector(state => state.settings?.isInitialized);
  const vehicles = useSelector(state => state.appData.vehicles);
  const currentUser = useSelector(state => state.auth?.user);
  const globalVehicleLimit = useSelector(state => state.settings?.global?.vehicleLimit);
  const operators = useSelector(state => state.operators?.operators);

  const isOperatorAccount = isOperatorRole(currentUser);
  const currentOperatorId = currentUser?.operatorId || null;
  const operatorsLoaded = (operators || []).length > 0;

  useEffect(() => {
    if (!settingsInitialized) dispatch(fetchSettings());
  }, [dispatch, settingsInitialized]);

  useEffect(() => {
    if (isOperatorAccount && !operatorsLoaded) dispatch(fetchOperators());
  }, [dispatch, isOperatorAccount, operatorsLoaded]);

  const vehicleLimitFromDB = useMemo(() => {
    if (isOperatorAccount) {
      const ownOperator = (operators || []).find(op => op.id === currentOperatorId);
      return ownOperator?.vehicleLimit;
    }
    return globalVehicleLimit;
  }, [isOperatorAccount, operators, currentOperatorId, globalVehicleLimit]);

  return useMemo(() => {
    const licenseInfo = getLicenseInfo(vehicleLimitFromDB);
    const all = vehicles || [];
    const vehicleCount = all.filter(isVehicleActive).length;
    return {
      isOperatorAccount,
      licenseInfo,
      vehicleCount,
      reserveCount: all.length - vehicleCount,
      isNearLimit: vehicleCount >= licenseInfo.limit * 0.8,
      isAtLimit: vehicleCount >= licenseInfo.limit,
      // Mehr Fahrzeuge als Kontingent: entsteht, wenn die Zentrale das
      // Kontingent nachträglich unter den Bestand senkt. Die vorhandenen
      // Fahrzeuge bleiben nutzbar, nur neue lassen sich nicht mehr anlegen —
      // „Limit erreicht: 5/1" wäre dafür eine irreführende Formulierung.
      isOverLimit: vehicleCount > licenseInfo.limit,
    };
  }, [vehicleLimitFromDB, vehicles, isOperatorAccount]);
};

export default useVehicleQuotaStatus;
